import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from '@/constants/routes';

/**
 * 共有端末用の自動タイムアウトフック
 * 指定秒数後に自動的にTOP画面に遷移する
 *
 * @param timeoutSeconds タイムアウト時間（秒）
 * @returns 残り秒数とタイマーリセット関数
 */
export const useSharedDeviceTimeout = (timeoutSeconds: number) => {
  const navigate = useNavigate();

  // タイムアウト終了時刻（ミリ秒）を絶対時間で保持
  const [endTime, setEndTime] = useState(
    () => Date.now() + timeoutSeconds * 1000,
  );

  const [remainingSeconds, setRemainingSeconds] = useState(timeoutSeconds);

  // タイマーをリセットする関数
  const resetTimer = useCallback(() => {
    setEndTime(Date.now() + timeoutSeconds * 1000);
    setRemainingSeconds(timeoutSeconds);
  }, [timeoutSeconds]);

  useEffect(() => {
    const timer = setInterval(() => {
      // 終了時刻から現在時刻を引いて残り秒数を計算（絶対時間ベース）
      const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setRemainingSeconds(remaining);

      // タイムアウトしたらTOP画面に遷移
      if (remaining <= 0) {
        navigate(ROUTE_PATH.SHARE_DEVICE);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime, navigate]);

  // ユーザーが操作したらタイマーをリセット
  useEffect(() => {
    const userEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
      'input',
    ];

    const handleUserActivity = () => {
      resetTimer();
    };

    // すべてのユーザーイベントにリスナーを登録
    userEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity);
    });

    return () => {
      // クリーンアップ
      userEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
    };
  }, [resetTimer]);

  return { remainingSeconds, resetTimer };
};
